import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  subscribeToAuthChanges,
  fetchUserProfile,
  upsertUserProfile,
  signInWithGoogle as svcSignIn,
  signOut as svcSignOut,
} from '../services/authService';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState('initializing'); // initializing | authenticated | unauthenticated | error
  const [error, setError] = useState(null);

  /**
   * Suscripción al estado de Firebase Auth.
   * Se ejecuta una sola vez al montar; el unsubscribe se devuelve en cleanup.
   */
  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges(async (fbUser) => {
      try {
        if (fbUser) {
          setFirebaseUser(fbUser);
          // upsertUserProfile es idempotente: crea el doc en el primer login
          // y lo sincroniza en los recurrentes. Lo llamamos acá (y no en
          // signInWithGoogle) para que el perfil exista SIEMPRE antes de
          // marcar 'authenticated' — eso elimina el race que dejaba profile
          // en null y rompía el onboarding.
          const userProfile = await upsertUserProfile(fbUser);
          setProfile(userProfile);
          setStatus('authenticated');
        } else {
          setFirebaseUser(null);
          setProfile(null);
          setStatus('unauthenticated');
        }
        setError(null);
      } catch (err) {
        console.error('[AuthContext] Auth state error:', err);
        setError(err);
        setStatus('error');
      }
    });

    return unsubscribe;
  }, []);

  const signIn = useCallback(async () => {
    try {
      setError(null);
      await svcSignIn();
      // El onAuthStateChanged dispara la actualización de estado.
    } catch (err) {
      console.error('[AuthContext] Sign in failed:', err);
      setError(err);
      throw err; // Re-throw para que la UI muestre toast
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await svcSignOut();
    } catch (err) {
      console.error('[AuthContext] Sign out failed:', err);
      setError(err);
      throw err;
    }
  }, []);

  /**
   * Refresca el perfil — útil tras unirse a un hogar.
   */
  const refreshProfile = useCallback(async () => {
    if (!firebaseUser) return;
    try {
      const updated = await fetchUserProfile(firebaseUser.uid);
      setProfile(updated);
    } catch (err) {
      console.error('[AuthContext] Profile refresh failed:', err);
      setError(err);
    }
  }, [firebaseUser]);

  const value = {
    firebaseUser,
    profile,
    status,
    error,
    isAuthenticated: status === 'authenticated',
    isLoading: status === 'initializing',
    hasFamily: Boolean(profile?.familyId),
    signIn,
    signOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};